import React, { useState, useEffect, useCallback } from 'react';
import { timetableService, formatTime12Hour } from '../services/TimetableService';
import './StudentTimetableView.css';

function StudentTimetableView({ student }) {
  const [timetable, setTimetable] = useState(null);
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [loading, setLoading] = useState(true);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const loadTimetable = useCallback(async () => {
    try {
      setLoading(true);
      setTimetable(null);

      const classId = student.classId;
      if (!classId) {
        setLoading(false);
        return;
      }

      const timetableData = await timetableService.getTimetable(classId);
      setTimetable(timetableData);
    } catch (error) {
      console.error('Error loading timetable:', error);
    } finally {
      setLoading(false);
    }
  }, [student.classId]);

  useEffect(() => {
    loadTimetable();
  }, [loadTimetable]);

  const getCurrentDay = () => {
    const today = new Date();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[today.getDay()];
  };

  const getCurrentClass = () => {
    if (!timetable?.entries) return null;
    const now = new Date();
    const today = getCurrentDay();

    return timetable.entries.find(entry => {
      if (entry.dayOfWeek !== today) return false;
      // Simple time comparison for 12-hour format
      const currentHour = now.getHours();
      const currentMin = now.getMinutes();
      const currentTotal = currentHour * 60 + currentMin;

      // Parse 12-hour time to minutes
      const parseTime = (timeStr) => {
        const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!match) return 0;
        let hour = parseInt(match[1], 10);
        const min = parseInt(match[2], 10);
        const period = match[3].toUpperCase();
        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
        return hour * 60 + min;
      };

      const startTotal = parseTime(entry.startTime);
      const endTotal = parseTime(entry.endTime);

      return currentTotal >= startTotal && currentTotal <= endTotal;
    });
  };

  const filteredTimetable = timetable?.entries?.filter(entry => entry.dayOfWeek === selectedDay) || [];
  const currentClass = getCurrentClass();
  const currentDay = getCurrentDay();

  return (
    <div className="student-timetable-view">
      <h2>Timetable</h2>

      {loading ? (
        <div className="loading">Loading timetable...</div>
      ) : !student.classId ? (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No Class Assigned</h3>
          <p>You are not assigned to a class yet.</p>
        </div>
      ) : !timetable || timetable.entries?.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No Timetable</h3>
          <p>Your class timetable has not been set up yet.</p>
        </div>
      ) : (
        <>
          {currentClass && (
            <div className="current-class-card">
              <div className="current-class-header">
                <span className="current-class-icon">📍</span>
                <h3>Current Class</h3>
              </div>
              <div className="current-class-info">
                <h4>{currentClass.subjectName}</h4>
                <p>{currentClass.room && `Room: ${currentClass.room}`}</p>
                <p>{formatTime12Hour(currentClass.startTime)} - {formatTime12Hour(currentClass.endTime)}</p>
              </div>
            </div>
          )}

          <div className="day-selector">
            {days.map(day => (
              <button
                key={day}
                className={`day-button ${selectedDay === day ? 'active' : ''} ${day === currentDay ? 'today' : ''}`}
                onClick={() => setSelectedDay(day)}
              >
                {day}
                {day === currentDay && <span className="today-badge">Today</span>}
              </button>
            ))}
          </div>

          <div className="timetable-list">
            {filteredTimetable.length === 0 ? (
              <div className="no-classes">
                <p>No classes scheduled for {selectedDay}</p>
              </div>
            ) : (
              filteredTimetable.map(entry => (
                <div key={entry.id} className="timetable-entry">
                  <div className="time-slot">
                    <span className="start-time">{formatTime12Hour(entry.startTime)}</span>
                    <span className="end-time">{formatTime12Hour(entry.endTime)}</span>
                  </div>
                  <div className="class-info">
                    <h4>{entry.subjectName}</h4>
                    {entry.room && <p>📍 {entry.room}</p>}
                    {entry.teacherName && <p>👨‍🏫 {entry.teacherName}</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default StudentTimetableView;
